// app/learn/path/[slug]/tutor.tsx
// =============================================================================
// Kursi Learn — AI Tutor Screen
// Full chat interface with Tutor / Teach / Test / Simple / Advanced modes
// =============================================================================

import { useCallback, useEffect, useRef, useState } from 'react';
import {
  ActivityIndicator,
  KeyboardAvoidingView,
  Platform,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TextInput,
  View,
} from 'react-native';
import { router, useLocalSearchParams } from 'expo-router';
import { theme } from '@/constants/theme';
import { LEARN_LAUNCH_CONFIG } from '@/constants/learnLaunchConfig';
import { RuntimeAsyncState } from '@/features/unified-learning/runtime/components/RuntimeAsyncState';

// ── Design tokens ─────────────────────────────────────────────────────────────

const BG       = '#0B0B14';
const SURFACE  = '#16142A';
const SURFACE2 = '#221F3D';
const SURFACE3 = '#2D2B50';
const TEXT     = '#F0EFF8';
const TEXT_MED = '#9896B4';
const TEXT_DIM = '#5E5C78';
const PRIMARY  = theme.colors.primary;
const GREEN    = '#10B981';
const AMBER    = '#F59E0B';

// ── Types ─────────────────────────────────────────────────────────────────────

type TutorMode = 'tutor' | 'teach' | 'test' | 'simple' | 'advanced';

type Message = {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
};

const MODES: { key: TutorMode; label: string; icon: string; description: string }[] = [
  { key: 'tutor',    label: 'מנחה',     icon: '◎', description: 'הסברים, הנחיה וצעד הבא' },
  { key: 'teach',    label: 'הסבר לי',  icon: '▷', description: 'הסבר למנחה — קבל משוב' },
  { key: 'test',     label: 'בחינה',    icon: '▣', description: 'המנחה בוחן אתכם בשאלות' },
  { key: 'simple',   label: 'פשוט',     icon: '○', description: 'הסברים בסיסיים ופשוטים' },
  { key: 'advanced', label: 'מתקדם',    icon: '◈', description: 'עומק טכני ותיאורטי' },
];

const QUICK_PROMPTS: Partial<Record<TutorMode, string[]>> = {
  tutor:    ['הסבר לי את הנושא המרכזי', 'מה חשוב לזכור?', 'איפה אני תקוע?', 'מה הצעד הבא?'],
  teach:    ['אני אנסה להסביר...', 'הנושא עוסק ב...', 'העיקרון הוא...'],
  test:     ['שאל אותי שאלה', 'שאלה קשה בבקשה', 'שאלה על הנושא האחרון'],
  simple:   ['הסבר בשפה פשוטה', 'תן לי דוגמה מהחיים', 'פרק לשלבים'],
  advanced: ['הסבר בעומק', 'מה הניואנסים?', 'חבר בין הנושאים'],
};

const SUPABASE_URL      = process.env.EXPO_PUBLIC_SUPABASE_URL      ?? '';
const SUPABASE_ANON_KEY = process.env.EXPO_PUBLIC_SUPABASE_ANON_KEY ?? '';

// ── Main component ─────────────────────────────────────────────────────────────

export default function TutorScreen() {
  const params    = useLocalSearchParams<{ slug: string; uid: string; worldId?: string }>();
  const slug      = typeof params.slug    === 'string' ? params.slug    : '';
  const userId    = typeof params.uid     === 'string' ? params.uid     : '';
  const worldId   = typeof params.worldId === 'string' ? params.worldId : '';

  const [mode, setMode]         = useState<TutorMode>('tutor');
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput]       = useState('');
  const [loading, setLoading]   = useState(false);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [showModes, setShowModes] = useState(false);

  const scrollRef = useRef<ScrollView>(null);

  // Greeting on mount
  useEffect(() => {
    setMessages([{
      id: 'greeting',
      role: 'assistant',
      content: 'שלום! אני המנחה האישי לקורס זה.\n\nתוכלו לשאול כל שאלה על החומר, לבקש הסברים, לתרגל יחד, או לבחון את הידע שלכם. השתמשו בכפתורי המצב למעלה כדי להחליף סגנון הוראה.\n\nמה תרצו ללמוד היום?',
      timestamp: new Date(),
    }]);
  }, []);

  useEffect(() => {
    scrollRef.current?.scrollToEnd({ animated: true });
  }, [messages]);

  const sendMessage = useCallback(async (text?: string) => {
    const msg = (text ?? input).trim();
    if (!msg || loading) return;

    const userMsg: Message = {
      id:        `user_${Date.now()}`,
      role:      'user',
      content:   msg,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMsg]);
    setInput('');
    setLoading(true);

    try {
      const res = await fetch(`${SUPABASE_URL}/functions/v1/kp-ai-tutor`, {
        method:  'POST',
        headers: {
          'Content-Type':  'application/json',
          'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
          'apikey':        SUPABASE_ANON_KEY,
        },
        body: JSON.stringify({
          world_id:   worldId,
          user_id:    userId,
          slug,
          message:    msg,
          mode,
          session_id: sessionId,
        }),
      });

      const data = await res.json();

      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }

      const aiMsg: Message = {
        id:        `ai_${Date.now()}`,
        role:      'assistant',
        content:   data.reply ?? data.error ?? 'שגיאה בקבלת תשובה',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, aiMsg]);
    } catch {
      setMessages((prev) => [...prev, {
        id:        `err_${Date.now()}`,
        role:      'assistant',
        content:   'אירעה שגיאה. בדוק את החיבור ונסה שוב.',
        timestamp: new Date(),
      }]);
    } finally {
      setLoading(false);
    }
  }, [input, loading, worldId, userId, slug, mode, sessionId]);

  const handleModeSwitch = (newMode: TutorMode) => {
    setMode(newMode);
    setShowModes(false);
    const modeDef = MODES.find((m) => m.key === newMode);
    if (modeDef) {
      setMessages((prev) => [...prev, {
        id:        `mode_${Date.now()}`,
        role:      'assistant',
        content:   `עברתי למצב **${modeDef.label}** ${modeDef.icon}\n${modeDef.description}`,
        timestamp: new Date(),
      }]);
    }
  };

  if (!LEARN_LAUNCH_CONFIG.advancedTools.tutor) {
    return (
      <RuntimeAsyncState
        state="degraded"
        title="Tutor AI זמין בהמשך"
        description="בטא זו מתמקדת בלולאת הליבה: שיעור → תרגול → סקירה → מבחן. Tutor ייפתח בהדרגה אחרי יציבות מלאה."
        actionLabel="חזרה לבית המסלול"
        onAction={() => {
          if (router.canGoBack()) router.back();
          else router.replace(slug ? `/learn/path/${slug}/home` as any : '/learn' as any);
        }}
      />
    );
  }

  const currentMode = MODES.find((m) => m.key === mode)!;
  const quickPrompts = QUICK_PROMPTS[mode] ?? [];

  return (
    <KeyboardAvoidingView
      style={s.root}
      behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      keyboardVerticalOffset={Platform.OS === 'ios' ? 88 : 0}
    >
      {/* ── Header ── */}
      <View style={s.header}>
        <Pressable onPress={() => router.back()} style={s.backBtn} accessibilityRole="button">
          <Text style={s.backText}>‹ חזרה</Text>
        </Pressable>
        <View style={s.headerCenter}>
          <Text style={s.headerTitle}>מורה AI</Text>
          <Pressable
            style={[s.modeChip, { borderColor: PRIMARY + '44', backgroundColor: PRIMARY + '14' }]}
            onPress={() => setShowModes((v) => !v)}
            accessibilityRole="button"
          >
            <Text style={s.modeChipIcon}>{currentMode.icon}</Text>
            <Text style={[s.modeChipText, { color: PRIMARY }]}>{currentMode.label}</Text>
            <Text style={s.modeChipArrow}>{showModes ? '▲' : '▼'}</Text>
          </Pressable>
        </View>
        <View style={{ width: 64 }} />
      </View>

      {/* ── Mode selector ── */}
      {showModes && (
        <View style={s.modePanel}>
          {MODES.map((m) => (
            <Pressable
              key={m.key}
              style={[s.modeOption, mode === m.key && { backgroundColor: PRIMARY + '14', borderColor: PRIMARY + '33' }]}
              onPress={() => handleModeSwitch(m.key)}
              accessibilityRole="button"
            >
              <Text style={s.modeOptionIcon}>{m.icon}</Text>
              <View style={s.modeOptionText}>
                <Text style={[s.modeOptionLabel, mode === m.key && { color: PRIMARY }]}>{m.label}</Text>
                <Text style={s.modeOptionDesc}>{m.description}</Text>
              </View>
              {mode === m.key && <Text style={[s.modeCheck, { color: PRIMARY }]}>✓</Text>}
            </Pressable>
          ))}
        </View>
      )}

      {/* ── Messages ── */}
      <ScrollView
        ref={scrollRef}
        style={s.messages}
        contentContainerStyle={s.messagesContent}
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
      >
        {messages.map((msg) => (
          <MessageBubble key={msg.id} message={msg} />
        ))}
        {loading && <TypingIndicator />}
      </ScrollView>

      {/* ── Quick prompts ── */}
      {quickPrompts.length > 0 && messages.length <= 2 && (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          style={s.quickBar}
          contentContainerStyle={s.quickBarContent}
        >
          {quickPrompts.map((prompt) => (
            <Pressable
              key={prompt}
              style={s.quickChip}
              onPress={() => sendMessage(prompt)}
              accessibilityRole="button"
            >
              <Text style={s.quickChipText}>{prompt}</Text>
            </Pressable>
          ))}
        </ScrollView>
      )}

      {/* ── Input bar ── */}
      <View style={s.inputBar}>
        <TextInput
          style={s.input}
          value={input}
          onChangeText={setInput}
          placeholder="שאל שאלה..."
          placeholderTextColor={TEXT_DIM}
          multiline
          maxLength={500}
          textAlign="right"
          onSubmitEditing={() => sendMessage()}
          returnKeyType="send"
          editable={!loading}
          accessibilityLabel="שדה קלט שאלה"
        />
        <Pressable
          style={[s.sendBtn, { backgroundColor: loading || !input.trim() ? SURFACE3 : PRIMARY }]}
          onPress={() => sendMessage()}
          disabled={loading || !input.trim()}
          accessibilityRole="button"
          accessibilityLabel="שלח שאלה"
        >
          {loading
            ? <ActivityIndicator size="small" color={TEXT_MED} />
            : <Text style={s.sendIcon}>›</Text>
          }
        </Pressable>
      </View>
    </KeyboardAvoidingView>
  );
}

// ── Message bubble ─────────────────────────────────────────────────────────────

function MessageBubble({ message }: { message: Message }) {
  const isUser = message.role === 'user';
  return (
    <View style={[s.msgRow, isUser ? s.msgRowUser : s.msgRowAI]}>
      {!isUser && (
        <View style={s.aiAvatar}>
          <Text style={s.aiAvatarText}>AI</Text>
        </View>
      )}
      <View style={[s.bubble, isUser ? s.bubbleUser : s.bubbleAI]}>
        <Text style={[s.bubbleText, isUser ? s.bubbleTextUser : s.bubbleTextAI]}>
          {message.content}
        </Text>
        <Text style={s.msgTime}>
          {message.timestamp.toLocaleTimeString('he-IL', { hour: '2-digit', minute: '2-digit' })}
        </Text>
      </View>
    </View>
  );
}

function TypingIndicator() {
  return (
    <View style={[s.msgRow, s.msgRowAI]}>
      <View style={s.aiAvatar}>
        <Text style={s.aiAvatarText}>AI</Text>
      </View>
      <View style={[s.bubble, s.bubbleAI, { paddingVertical: 14, paddingHorizontal: 18 }]}>
        <Text style={{ color: TEXT_MED, fontSize: 20, letterSpacing: 4 }}>···</Text>
      </View>
    </View>
  );
}

// ── Styles ─────────────────────────────────────────────────────────────────────

const s = StyleSheet.create({
  root: { flex: 1, backgroundColor: BG },

  header: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingTop: Platform.OS === 'web' ? 20 : 16,
    paddingBottom: 12,
    borderBottomWidth: 1,
    borderBottomColor: SURFACE3,
    gap: 8,
  },
  backBtn: { paddingHorizontal: 8, paddingVertical: 6 },
  backText: { color: PRIMARY, fontFamily: theme.fonts.medium, fontSize: 15 },
  headerCenter: { flex: 1, alignItems: 'center', gap: 6 },
  headerTitle: { color: TEXT, fontFamily: theme.fonts.bold, fontSize: 16, textAlign: 'center' },
  modeChip: {
    flexDirection: 'row-reverse', alignItems: 'center', gap: 5,
    borderRadius: 99, borderWidth: 1,
    paddingHorizontal: 12, paddingVertical: 5,
  },
  modeChipIcon: { fontSize: 13 },
  modeChipText: { fontFamily: theme.fonts.bold, fontSize: 12 },
  modeChipArrow: { color: TEXT_DIM, fontSize: 9 },

  modePanel: {
    backgroundColor: SURFACE,
    borderBottomWidth: 1,
    borderBottomColor: SURFACE3,
    paddingHorizontal: 12,
    paddingVertical: 8,
    gap: 4,
  },
  modeOption: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 10,
    borderRadius: 10,
    borderWidth: 1,
    borderColor: 'transparent',
    paddingHorizontal: 12,
    paddingVertical: 10,
  },
  modeOptionIcon: { fontSize: 18, width: 24, textAlign: 'center' },
  modeOptionText: { flex: 1, gap: 2 },
  modeOptionLabel: { fontFamily: theme.fonts.bold, fontSize: 14, color: TEXT, textAlign: 'right' },
  modeOptionDesc:  { fontFamily: theme.fonts.regular, fontSize: 11, color: TEXT_MED, textAlign: 'right' },
  modeCheck: { fontFamily: theme.fonts.bold, fontSize: 14 },

  messages: { flex: 1 },
  messagesContent: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    gap: 12,
    maxWidth: 680,
    alignSelf: 'center' as any,
    width: '100%',
  },

  msgRow: { flexDirection: 'row-reverse', alignItems: 'flex-end', gap: 8 },
  msgRowUser: { justifyContent: 'flex-start' },
  msgRowAI:   { justifyContent: 'flex-end' },

  aiAvatar: {
    width: 32, height: 32, borderRadius: 16,
    backgroundColor: PRIMARY + '22', borderWidth: 1, borderColor: PRIMARY + '44',
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  aiAvatarText: { color: PRIMARY, fontFamily: theme.fonts.bold, fontSize: 10 },

  bubble: {
    maxWidth: '80%', borderRadius: 18, paddingHorizontal: 14, paddingVertical: 10, gap: 4,
  },
  bubbleUser: {
    backgroundColor: PRIMARY,
    borderBottomRightRadius: 4,
  },
  bubbleAI: {
    backgroundColor: SURFACE,
    borderWidth: 1,
    borderColor: SURFACE3,
    borderBottomLeftRadius: 4,
  },
  bubbleText: {
    fontFamily: theme.fonts.regular, fontSize: 14, lineHeight: 22,
  },
  bubbleTextUser: { color: '#fff', textAlign: 'right' },
  bubbleTextAI:   { color: TEXT,   textAlign: 'right' },
  msgTime: {
    fontFamily: theme.fonts.regular, fontSize: 10, color: TEXT_DIM,
    textAlign: 'right',
  },

  quickBar: { maxHeight: 44 },
  quickBarContent: {
    paddingHorizontal: 16, gap: 8, paddingVertical: 6,
    flexDirection: 'row-reverse',
  },
  quickChip: {
    backgroundColor: SURFACE2, borderWidth: 1, borderColor: SURFACE3,
    borderRadius: 99, paddingHorizontal: 14, paddingVertical: 7,
    flexShrink: 0,
  },
  quickChipText: { fontFamily: theme.fonts.medium, fontSize: 12, color: TEXT_MED },

  inputBar: {
    flexDirection: 'row-reverse',
    alignItems: 'flex-end',
    paddingHorizontal: 16,
    paddingVertical: 10,
    paddingBottom: Platform.OS === 'ios' ? 28 : 14,
    borderTopWidth: 1,
    borderTopColor: SURFACE3,
    backgroundColor: BG,
    gap: 8,
  },
  input: {
    flex: 1,
    backgroundColor: SURFACE,
    borderWidth: 1,
    borderColor: SURFACE3,
    borderRadius: 20,
    paddingHorizontal: 16,
    paddingVertical: 10,
    fontFamily: theme.fonts.regular,
    fontSize: 14,
    color: TEXT,
    maxHeight: 100,
    textAlign: 'right',
  },
  sendBtn: {
    width: 42, height: 42, borderRadius: 21,
    alignItems: 'center', justifyContent: 'center', flexShrink: 0,
  },
  sendIcon: { color: '#fff', fontSize: 22, fontWeight: '700' as any, marginLeft: -2 },
});
